import React from "react";
import {
  Ruler,
  CheckCircle2,
  FileText,
  ShieldCheck,
  Users,
  Calendar,
  LayoutGrid,
} from "lucide-react";
import HeaderMenu from "../../component/Navbar/headermenu";
import CustomFooter from "../../component/Footer/customFooter";
import ShowcaseImageLightbox from "../../component/ShowcaseImageLightbox";
import "./about.css";
import dashboard from "../../assets/new-images/Dashboard.png";
import taskList from "../../assets/new-images/TaskManag2.png";
import taskDetail from "../../assets/new-images/t2.png";

const features = [
  {
    icon: <Ruler size={26} />,
    title: "Milestone & Phase Tracking",
    description:
      "Organize projects from concept design and schematic layout to construction documentation and final site handover.",
  },
  {
    icon: <FileText size={26} />,
    title: "Drawing & Version Control",
    description:
      "Manage drawing sets, revisions, and status sheets with clear version logging and assignee tagging.",
  },
  {
    icon: <ShieldCheck size={26} />,
    title: "Principal & Partner Reviews",
    description:
      "Standardize internal design reviews and client approval steps before submitting drafts.",
  },
  {
    icon: <Users size={26} />,
    title: "Client Collaboration",
    description:
      "Provide a secure workspace to share blueprints, design mockups, and progress updates with clients.",
  },
];

const ArchitectureFirmsPage = () => {
  const showcase = [
    {
      eyebrow: "Project & Milestone Tracking",
      title: "Every project, drawing, and milestone in one place",
      description:
        "Design drafts, client approvals, site visits, and submission deadlines — all tracked as tasks with project, status, and due date, so every phase moves forward without a status meeting.",
      image: taskList,
      alt: "Simplifyte task board showing projects, clients, status, period, and financial year with To Do, In Progress, Under Review, and Closed counts",
    },
    {
      eyebrow: "Task-level detail",
      title: "Every milestone, with the project context that matters",
      description:
        "Open any task to see the project, phase, due date, and assignee at a glance — so a drawing revision or site milestone carries exactly the context your team needs, without a separate lookup.",
      image: taskDetail,
      alt: "Simplifyte task peek panel open beside the task board, showing client, financial year, period, due date, and assignee for the selected task",
    },
    {
      eyebrow: "Project Dashboard",
      title: "See where every project stands, every morning",
      description:
        "Today's tasks, a simple performance percentage, and a live breakdown across To Do, In Progress, Under Review, and Closed — plus upcoming events and reminders — so principals start the day already knowing which projects need attention.",
      image: dashboard,
      alt: "Simplifyte dashboard showing today's tasks, performance percentage, task status breakdown, and upcoming events and reminders",
    },
  ];

  const benefits = [
    "Keep every project phase and milestone on one timeline",
    "Cut back-and-forth on drawing revisions and approvals",
    "Give principals full visibility across every active project",
    "Standardize how site visits and submissions get tracked",
    "Hit client and regulatory deadlines without last-minute scrambles",
  ];

  return (
    <>
      <HeaderMenu />
      <div className="Industry_class_main">
        {/* Hero */}
        <section className="Industry_class_hero">
          <div className="Industry_class_container">
            <div className="Industry_class_hero_content">
              <div className="Industry_class_icon_box">
                <Ruler size={34} />
              </div>

              <h1 className="Industry_class_heading">
                Project and milestone management software for Architecture
                &amp; Engineering firms
              </h1>

              <p className="Industry_class_subheading">
                Track drawings, approvals, site milestones, and client
                sign-offs from concept to completion.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="Industry_class_features_section">
          <div className="Industry_class_container">
            <h2 className="Industry_class_section_title Industry_class_center">
              Everything architecture teams need in one platform
            </h2>

            <div className="Industry_class_features_grid">
              {features.map((feature, index) => (
                <div key={index} className="Industry_class_feature_card">
                  <div className="Industry_class_feature_icon">
                    {feature.icon}
                  </div>

                  <h3 className="Industry_class_feature_title">
                    {feature.title}
                  </h3>

                  <p className="Industry_class_feature_description">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Showcase */}
        <section className="Industry_class_showcase_section">
          <div className="Industry_class_container">
            <h2 className="Industry_class_section_title Industry_class_center">
              Everything you need to run your projects
            </h2>
            <p className="Industry_class_showcase_subtitle">
              See how architecture and engineering teams track projects,
              drawings, and approvals on Simplifyte.
            </p>

            <div className="Industry_class_showcase_list">
              {showcase.map((item, index) => (
                <div
                  key={index}
                  className={`Industry_class_showcase_row ${
                    index % 2 === 1 ? "Industry_class_showcase_row_reverse" : ""
                  }`}
                >
                  <div className="Industry_class_showcase_text">
                    <span className="Industry_class_showcase_eyebrow">
                      {item.eyebrow}
                    </span>
                    <h3 className="Industry_class_showcase_title">
                      {item.title}
                    </h3>
                    <p className="Industry_class_showcase_description">
                      {item.description}
                    </p>
                  </div>

                  <div className="Industry_class_showcase_media">
                    <div className="Industry_class_showcase_frame_outer">
                      <div className="Industry_class_showcase_frame_inner">
                        <ShowcaseImageLightbox
                          src={item.image}
                          alt={item.alt}
                          className="Industry_class_showcase_image"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="Industry_class_benefits_section">
          <div className="Industry_class_container">
            <div className="Industry_class_benefits_wrapper">
              <h2 className="Industry_class_section_title Industry_class_center">
                Why architecture &amp; engineering firms choose Simplifyte
              </h2>

              <div className="Industry_class_benefits_list">
                {benefits.map((benefit, index) => (
                  <div key={index} className="Industry_class_benefit_item">
                    <CheckCircle2
                      size={24}
                      className="Industry_class_check_icon"
                    />

                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="Industry_class_cta_section">
          <div className="Industry_class_container">
            <div className="Industry_class_cta_box">
              <h2 className="Industry_class_cta_title">
                Ready to simplify your projects?
              </h2>

              <p className="Industry_class_cta_text">
                Join architecture and engineering firms who've brought
                clarity to drawings, approvals, and milestones.
              </p>

              <button
                className="Industry_class_cta_button"
                onClick={() =>
                  (window.location.href = "https://app.simplifyte.com")
                }
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </section>
      </div>
      <CustomFooter />
    </>
  );
};

export default ArchitectureFirmsPage;
